/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import { isEmpty, toArray } from 'lodash'
import classNames from 'classnames'

import { Divider } from '@/components/dividers/Divider'

import { useQuery } from '@/utils/hooks/useQuery'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Pill } from '@/components/pills/Pill'
import { setMassPlaybookUpdateSelection } from '@/reducers/playbooks/playbooks.redux'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import {
  categoryEntryInitialValues,
  categoryEntryValidationSchema,
  modifyExistingCategoryEntryInitialValues,
} from './categoryEntry.schema'
import { CategoryEntryForm } from './CategoryEntryForm'
import { PlaybookPreview } from '../../components/PlaybookPreview'
import { PlaybookPreviewSelectable } from '../../components/PlaybookPreviewSelectable'
import { PlaybookEditor } from '../../components/PlaybookEditor'
import { ChecklistEntryPreview } from '../checklist/ChecklistEntryPreview'
import {
  accessors,
  accessorSectionMap,
  massPlaybookUpdateProgressStatus,
  postcallTriggerTypes,
  sides,
} from '../../playbook.helpers'
import { getColumns } from './CategoryBulkEditColumns'
import { LoadingPlaybooks, SelectionActions } from '../shared/MassPlaybookUpdateComponents'
import './CategoryEntry.scss'

const PreviewContent = ({ values, accessor }) => {
  if (!values || !values.display) {
    return null
  }
  const entry = modifyExistingCategoryEntryInitialValues(accessor)(values)
  return (
    <>
      {entry.display.header && <h3>{entry.display.header}</h3>}
      {entry.display.decklist?.order && entry.display.decklist?.entries && (
        <div className="checklist-entries">
          {toArray(entry.display.decklist.order).map((existingEntryId) => {
            const checklistEntry = entry.display.decklist.entries[existingEntryId]

            if (checklistEntry) {
              return (
                <ChecklistEntryPreview
                  key={existingEntryId}
                  isEditEntry
                  entry={checklistEntry}
                  accessor={accessor}
                  inlineVerbatim
                />
              )
            }

            return null
          })}
        </div>
      )}
      {entry.display?.footer && <h3>{entry.display.footer}</h3>}
      {entry.display?.html && !isEmpty(entry.display?.decklist?.order) && <Divider />}
      {entry.display?.html && <div dangerouslySetInnerHTML={{ __html: entry.display.html }} />}
    </>
  )
}

export const CategoryEntryEditor = () => {
  const dispatch = useDispatch()
  const { entryId, section } = useParams()

  const { organizationid: organizationId, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )
  const { loading, massPlaybookUpdate, playbooks } = useSelector((state) => state.playbooks)
  const { playbook, listIDsInDB = {} } = useSelector((state) => state.playbook)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { selectedHierarchyOrganizations } = useSelector((state) => state.analytics)
  const accessor = accessorSectionMap[section]

  const { isEnabled, progress, matches, fetchFailed, selection, categorySelection } =
    massPlaybookUpdate || {}
  const isExistingItem = listIDsInDB[accessor].includes(entryId)

  const query = useQuery()
  const isCreate = entryId === 'create'
  const categoryId = query.get('category_id')
  const recommendation = query.get('recommendation')
  const categoryName = playbook.body[accessor]?.entries[categoryId]?.name
  const entry = isCreate ? null : playbook.body[accessor]?.entries[categoryId]?.entries[entryId]
  const showMassPlaybookUpdateContent = !loading && isEnabled
  const isBaltoAdmin = organizationId === 1
  const organizationIds = flattenedUserOrgHierarchy.map((org) => org.organization_id)
  const canSeeOrgHierarchy = !isBaltoAdmin && isHierarchyManager && organizationIds.length > 1

  // Mass Playbook Update matches pagination
  const [activePage, setActivePage] = useState(1)
  const rowsPerPage = 5

  const playbookDetails = (playbookId, contentId) => {
    const orgPlaybook = playbooks.find((p) => p.id.toString() === playbookId)

    if (orgPlaybook) {
      const categories = orgPlaybook.body[accessor]?.entries ?? {}

      const category = Object.values(categories).find((cat) => cat.entries?.[contentId])
      return category?.entries?.[contentId]
    }
    return null
  }

  const matchesExtended = matches.map((match) => {
    const matchPlaybook = playbookDetails(match.configIdOriginal, match.contentIdOriginal)
    const triggerType =
      [...postcallTriggerTypes, { label: 'Smart Phrases', value: 'smart_phrases' }].find(
        (t) => t.value === matchPlaybook?.trigger?.type
      )?.label || '-'
    const side = sides.find((t) => t.value === matchPlaybook?.trigger?.side)?.label || '-'

    return {
      ...match,
      triggerType,
      side,
    }
  })

  // Used for Dynamic Prompts (edit)
  const paginatedMatches = useMemo(
    () =>
      matchesExtended
        .filter(
          (m) =>
            isEmpty(selectedHierarchyOrganizations) ||
            selectedHierarchyOrganizations.includes(m.organizationId)
        )
        .slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage),
    [matches, activePage, selectedHierarchyOrganizations]
  )

  // Used for Post Call (add/update) & Dynamic Prompts (add)
  const matchesFiltered = matchesExtended.filter(
    (m) =>
      isEmpty(selectedHierarchyOrganizations) ||
      selectedHierarchyOrganizations.includes(m.organizationId)
  )
  const count = matchesFiltered.length
  const totalPages = Math.ceil(count / rowsPerPage)
  const allSelected = matchesFiltered.length === selection.length

  const handleSelectAll = () => {
    if (progress !== massPlaybookUpdateProgressStatus.INITIAL) return
    if (allSelected) {
      dispatch(setMassPlaybookUpdateSelection([]))
    } else {
      dispatch(setMassPlaybookUpdateSelection(matchesFiltered.map((row) => row.compoundId)))
    }
  }

  const handleSelectSingle = (compoundId) => {
    if (progress !== massPlaybookUpdateProgressStatus.INITIAL) return
    if (!selection.includes(compoundId)) {
      dispatch(setMassPlaybookUpdateSelection([...selection, compoundId]))
    } else {
      dispatch(
        setMassPlaybookUpdateSelection(selection.filter((selectedId) => selectedId !== compoundId))
      )
    }
  }

  const validCategorySelectionOnAdd = useMemo(() => {
    if (!isEnabled || isExistingItem) return true

    const selectedPlaybookIds = selection.map((selected) => {
      return selected.split('_')[0]
    })
    return selectedPlaybookIds.every((playbookId) => !isEmpty(categorySelection[playbookId]))
  }, [isEnabled, isExistingItem, selection, categorySelection])

  useEffect(() => {
    // Check if some organization is removed (from organization picker) and then remove matches that belong to that org in selection
    const toRemove = []
    selection.forEach((compoundId) => {
      const match = matches.find((m) => m.compoundId === compoundId)
      if (match && !selectedHierarchyOrganizations.includes(match.organizationId))
        toRemove.push(match.compoundId)
    })
    if (!isEmpty(toRemove)) {
      const newSelection = selection.filter((compoundId) => !toRemove.includes(compoundId))
      dispatch(setMassPlaybookUpdateSelection(newSelection))
    }
  }, [selectedHierarchyOrganizations])

  return (
    <PlaybookEditor entry={entry} categoryId={categoryId}>
      {({ handleUpdate, handleCancel, readOnly }) => {
        const initialValues = isCreate
          ? categoryEntryInitialValues(accessor)
          : modifyExistingCategoryEntryInitialValues(accessor)(entry)

        return (
          <Formik
            initialValues={initialValues}
            validationSchema={categoryEntryValidationSchema(accessor)}
            onSubmit={handleUpdate}
            enableReinitialize
          >
            {({ values, handleSubmit, errors, ...formikProps }) => (
              <>
                <CategoryEntryForm
                  readOnly={readOnly}
                  values={values}
                  errors={errors}
                  entryId={entryId}
                  handleSubmit={handleSubmit}
                  handleCancel={handleCancel}
                  accessor={accessor}
                  categoryName={categoryName}
                  recommendation={recommendation}
                  validCategorySelectionOnAdd={validCategorySelectionOnAdd}
                  {...formikProps}
                />
                <div className="playbook-category-section__scrollable">
                  {accessor === accessors.DYNAMIC_PROMPT && (
                    <PlaybookPreview header="PREVIEW" values={values} section={section}>
                      <PreviewContent values={values} accessor={accessor} />
                    </PlaybookPreview>
                  )}
                  <LoadingPlaybooks loading={loading} isEnabled={isEnabled} />
                  {showMassPlaybookUpdateContent &&
                    // Show table: Post Call ADD / EDIT item | Dynamic Prompt ADD item
                    (accessor === accessors.POSTCALL ||
                    (accessor === accessors.DYNAMIC_PROMPT && !isExistingItem) ? (
                      <>
                        <div className="flex-center mtl">
                          <Pill small emphasized>
                            Other Playbooks
                          </Pill>
                        </div>
                        <div className="padding-large">
                          <div
                            className={classNames('post-call-table-wrap', {
                              'post-call-table-wrap--loading': isEmpty(matchesExtended),
                            })}
                          >
                            {!isEmpty(matchesFiltered) && !fetchFailed && (
                              <p style={{ fontSize: '14px', fontWeight: 600 }}>
                                {`Select at least one playbook to ${isExistingItem ? 'edit' : 'add'} ${accessor === accessors.DYNAMIC_PROMPT ? 'Dynamic Prompt' : 'Post Call'} in bulk.`}
                              </p>
                            )}
                            <AdvancedTable
                              columns={getColumns(
                                isExistingItem,
                                progress,
                                canSeeOrgHierarchy,
                                section
                              )}
                              rows={matchesFiltered}
                              selectedRows={selection}
                              index="compoundId"
                              bulkSelectable={progress === massPlaybookUpdateProgressStatus.INITIAL}
                              bulkSelectAction
                              customBulkSelectBanner={
                                <SelectionActions
                                  selection={selection}
                                  allSelected={allSelected}
                                  matches={matchesFiltered}
                                  handleSelectAll={handleSelectAll}
                                  isOnTop
                                />
                              }
                              handleBulkSelectSingle={handleSelectSingle}
                              handleDeselectAll={handleSelectAll}
                              handleSelectAll={handleSelectAll}
                              disabledSelection={
                                progress !== massPlaybookUpdateProgressStatus.INITIAL
                              }
                              itemName="item"
                              noDataHeader={
                                fetchFailed
                                  ? 'Failed to get phrase suggestions for bulk editing'
                                  : undefined
                              }
                              noDataText={
                                fetchFailed
                                  ? 'Please retry by reloading or reopening the page'
                                  : 'No similar content found across your Playbooks'
                              }
                              wrapCss={{ overflow: 'inherit' }}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      // Show selecatable similar content: Dynamic Prompt EDIT item
                      <>
                        <div className="flex-center mtl">
                          <Pill small emphasized>
                            Similar Content
                          </Pill>
                        </div>
                        <SelectionActions
                          selection={selection}
                          allSelected={allSelected}
                          matches={matchesFiltered}
                          handleSelectAll={handleSelectAll}
                        />
                        {count > rowsPerPage && (
                          <div className="preview-paging-top">
                            <AdvancedTablePagination
                              activePage={activePage}
                              rowsPerPage={rowsPerPage}
                              count={count}
                              totalPages={totalPages}
                              setActivePage={setActivePage}
                              transparent
                            />
                          </div>
                        )}
                        {paginatedMatches.map((match) => (
                          <PlaybookPreviewSelectable
                            key={match.compoundId}
                            match={match}
                            section={section}
                            handleSelectSingle={handleSelectSingle}
                            showOrg={canSeeOrgHierarchy}
                          >
                            <div className="preview-trigger-side">{`${match.triggerType} | ${match.side}`}</div>
                            <PreviewContent
                              values={playbookDetails(
                                match.configIdOriginal,
                                match.contentIdOriginal
                              )}
                              accessor={accessor}
                            />
                          </PlaybookPreviewSelectable>
                        ))}
                        {count > rowsPerPage && (
                          <div className="preview-paging">
                            <AdvancedTablePagination
                              activePage={activePage}
                              rowsPerPage={rowsPerPage}
                              count={count}
                              totalPages={totalPages}
                              setActivePage={setActivePage}
                              transparent
                            />
                          </div>
                        )}
                      </>
                    ))}
                </div>
              </>
            )}
          </Formik>
        )
      }}
    </PlaybookEditor>
  )
}
